import dayjs from 'dayjs'

/**
 * Check if a plan year start date is "active" (within 1 year from today)
 * Active means: today is within the plan year period (start date to start date + 1 year)
 * @param {string} dateStr - Date in format MM/DD/YYYY
 * @returns {boolean} True if the date is active
 */
export function isActivePlanYearDate(dateStr) {
  if (!dateStr) return false
  
  const [month, day, year] = dateStr.split('/').map(Number)
  const planStartDate = new Date(year, month - 1, day)
  const planEndDate = new Date(year + 1, month - 1, day) // 1 year after start
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  // Active if today is between plan start and plan end (inclusive)
  return today >= planStartDate && today < planEndDate
}

/**
 * Get all active plan year start dates from options array
 * @param {Array<{value: string, label: string}>} planYearStartDates - Array of date options
 * @returns {string[]} Array of active date strings
 */
export function getActivePlanYearDates(planYearStartDates) {
  return planYearStartDates
    .filter(option => isActivePlanYearDate(option.value))
    .map(option => option.value)
}

/**
 * Get plan year start dates grouped by Active/Inactive status
 * @param {Array<{value: string, label: string}>} planYearStartDates - Array of date options
 * @returns {{ active: Array, inactive: Array }} Grouped options
 */
export function getGroupedPlanYearStartDates(planYearStartDates) {
  const active = []
  const inactive = []
  
  planYearStartDates.forEach(option => {
    if (isActivePlanYearDate(option.value)) {
      active.push(option)
    } else {
      inactive.push(option)
    }
  })
  
  // Sort by date descending (most recent first)
  const sortByDateDesc = (a, b) => {
    const [mA, dA, yA] = a.value.split('/').map(Number)
    const [mB, dB, yB] = b.value.split('/').map(Number)
    const dateA = new Date(yA, mA - 1, dA)
    const dateB = new Date(yB, mB - 1, dB)
    return dateB - dateA
  }
  
  active.sort(sortByDateDesc)
  inactive.sort(sortByDateDesc)
  
  return { active, inactive }
}

/**
 * Enriches each employer object with upcoming compliance events and plan year state in three formats:
 * 1. `upcomingEvents` – names of up to three events concatenated by comma (for table cell).
 * 2. `upcomingEventsArray` – array of { name, date } used by tooltip to render name + formatted date.
 * 3. `isPlanYearExpired` – boolean indicating whether the employer's current plan year has already ended.
 *
 * The algorithm selects events occurring within the next 30 days; if none, it takes the next closest
 * future event(s). Dates are evaluated in the current calendar year and, if already past, rolled over
 * to the next year to ensure a future occurrence.
 *
 * @param {Array<Object>} employers - Array of employer objects to mutate in place.
 */
export function addUpcomingEventsToEmployers(employers = []) {
  const today = dayjs()
  // Natural sort collator (numeric + base sensitivity)
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  employers.forEach(emp => {
    // Determine the effective date: plan start date if in the future, otherwise today.
    const planStartDate = dayjs(emp.plan_start_date, 'MM/DD/YYYY')
    const effectiveDate = planStartDate.isAfter(today, 'day') ? planStartDate : today
    const in30Days = effectiveDate.add(30, 'day')
    const upperLimit = planStartDate.add(1, 'year').subtract(1, 'day')

    emp.isPlanYearExpired = upperLimit.isBefore(today, 'day')

    const events = emp.complianceEvents?.results || []

    // Map each event to an absolute Dayjs date, relative to the effectiveDate's year
    const parsed = events.map(e => {
      let parsedDate = dayjs(`${e.date}/${effectiveDate.year()}`, 'M/D/YYYY')
      // If the date has passed this year relative to the effective date, shift to next year
      if (parsedDate.isBefore(effectiveDate, 'day')) {
        parsedDate = parsedDate.add(1, 'year')
      }
      return { ...e, parsedDate }
    })
    // Case 1: Find up to 3 events in the 30 days following the effectiveDate
    const upcomingIn30Days = parsed
      .filter(e => 
        !e.parsedDate.isBefore(effectiveDate, 'day') &&
        !e.parsedDate.isAfter(in30Days, 'day') &&
        !e.parsedDate.isAfter(upperLimit, 'day')
      )
      .sort((a, b) => a.parsedDate - b.parsedDate)

    let finalEvents = upcomingIn30Days.slice(0, 3)

    // Case 2: Fallback if no events in the next 30 days
    if (finalEvents.length === 0) {
      const allFutureEvents = parsed
        .filter(e => 
          !e.parsedDate.isBefore(effectiveDate, 'day') &&
          !e.parsedDate.isAfter(upperLimit, 'day')
        )
        .sort((a, b) => a.parsedDate - b.parsedDate)

      if (allFutureEvents.length > 0) {
        const closestDate = allFutureEvents[0].parsedDate
        const eventsOnClosestDate = allFutureEvents.filter(e => e.parsedDate.isSame(closestDate, 'day'))

        // Sort by name alphabetically and pick the first one
        eventsOnClosestDate.sort((a, b) => collator.compare(a.name, b.name));
        finalEvents = [eventsOnClosestDate[0]]
      }
    }
    
    // Group final events by date for the tooltip
    const groupedEvents = finalEvents.reduce((acc, event) => {
      const dateKey = event.parsedDate.format('YYYY-MM-DD')
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: event.parsedDate.toDate(),
          events: []
        }
      }
      acc[dateKey].events.push({
        id: event.id,
        name: event.name,
        body: event.body,
        date: event.date // Original date string (e.g., "3/26")
      })
      return acc
    }, {})

    // Sort events within each date group alphabetically
    Object.values(groupedEvents).forEach(group => {
      group.events.sort((a, b) => collator.compare(a.name, b.name))
    })
    emp.upcomingEventsArray = Object.values(groupedEvents)
    // Build sorted flat string for upcomingEvents
    emp.upcomingEvents = emp.upcomingEventsArray
      .flatMap(group => group.events.map(e => e.name))
      .join(', ')
  })
}
